import React, { useState, useEffect, useMemo, useCallback } from 'react';
import "../components/styles/messagebox.css"
import NoMessage from './NoMessage';
import Loader from './Loader';
import MessageBoxHeader from './MessageBoxHeader';
import ViewMessageHeader from './ViewMessageHeader';
import Messages from './Messages';
import Encrypt from './emails/Encrypt';
import ComposeEmail from '../components/emails/ComposeEmail.js';
import Decrypt from './emails/Decrypt';
import { useSelector, useDispatch } from 'react-redux';
import { getGeneralSettings } from '../helper/settingsHelper.js';
import { buildRenderList, DEFAULT_SORT } from '../helper/messageListHelper.js';
import { reportError, toErrorString } from '../utils/errorLogger.js';


const CommonMessage = (props) => {
  const dispatch = useDispatch();
  const getCompose = useSelector((state) => state.isCompose);
  const [selectedOption, setSelectedOption] = useState('all');
  const [selectedMessageIndex, setSelectedMessageIndex] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isCompose, setIsCompose] = useState(getCompose || false);
  const [isDecrypted, setIsDecrypted] = useState(false);
  const [decryptedContent, setDecryptedContent] = useState(null);
  const [receiverNameValue] = useState("");
  const [isGrouped, setIsGrouped] = useState(false);
  const getFilters = useSelector((state) => state.filterObject);
  const searchTerm = useSelector((state) => state.searchTerm);
  const sortType = useSelector((state) => state.sortType) || DEFAULT_SORT;
  const refreshSettings = useSelector((state) => state.refreshSettings);
  const type = props.pageType || "Inbox";
  const messageViewStatus = !(selectedMessageIndex === null);
  const isMessageLoad = !props.loader && messageViewStatus;

  const messageList = useMemo(() => props.messageList || [], [props.messageList]);
  const readList = useMemo(() => props.readList || [], [props.readList]);
  const unreadList = useMemo(() => props.unreadList || [], [props.unreadList]);

  // Conversation grouping is an account setting, so it is read once per session (and again when the
  // settings screen saves) instead of on every list change — it is a chain call.
  useEffect(() => {
    let cancelled = false;
    const fetchSettings = async () => {
      try {
        const generalSettings = await getGeneralSettings();
        if (!cancelled) setIsGrouped(!!generalSettings?.isGrouped);
      } catch (err) {
        const errorStr = toErrorString(err || err.message);
        reportError({
          endpoint: '-',
          message: `FileName: CommonMessage.js, Fun: fetchSettings, ${String(err.message || 'Unhandled error')}`,
          error: errorStr,
          userName: localStorage.getItem('UserName') || '',
        });
        console.error(err);
      }
    };
    fetchSettings();
    return () => { cancelled = true; };
  }, [refreshSettings]);

  const baseList = useMemo(() => {
    switch (selectedOption) {
      case "read":
        return readList;
      case "unread":
        return unreadList;
      // Matches the mobile client's filter set (All / Read / Unread / Starred). Unlike the sidebar's
      // Favourite page this composes with the mailbox you are already in, so you can see starred mail
      // inside Sent, Archive or a folder.
      case "starred":
        return messageList.filter((message) => message?.isStarred);
      default:
        return messageList;
    }
  }, [selectedOption, messageList, readList, unreadList]);

  // The single source of truth for what is on screen. Show/Read/Unread, the advanced From/To/Subject/
  // Date filter, the search box, the sort control and conversation grouping all compose here in a
  // fixed order, so no control can silently undo another one — and the identity is stable, which is
  // what stops the list component re-rendering in a loop.
  const renderMessageList = useMemo(
    () => buildRenderList({ base: baseList, filters: getFilters, searchTerm, sortType, isGrouped }),
    [baseList, getFilters, searchTerm, sortType, isGrouped]
  );

  const refreshCount = useSelector((state) => state.refreshCount);
  useEffect(() => {
    if (isDecrypted) {
      dispatch({ type: "REFRESH_COUNT", payload: !refreshCount });
    }
  }, [isDecrypted]);

  const draftData = useSelector((state) => state.draftData);
  const outBoxData = useSelector((state) => state.outBoxData);
  const outBoxIndex = useSelector((state) => state.outBoxIndex);
  const selectMessage = useSelector((state) => state.selectedMessage);

  useEffect(() => {
    if (draftData) {
      dispatch({ type: "IS_COMPOSE", payload: true })
    }

  }, [draftData]);

  useEffect(() => {
    if (outBoxData) {
      dispatch({ type: "IS_COMPOSE", payload: true })
      dispatch({ type: "OUTBOX", payload: outBoxIndex, value: false });
    }
  }, [outBoxData]);

  React.useEffect(() => {
    setIsCompose(getCompose);
  }, [getCompose]);

  const handleOptionChange = useCallback((event) => {
    setSelectedOption(event.target.value);
  }, []);

  const reRender = useCallback(async () => {
    if (props.reRender) props.reRender();
  }, [props.reRender]);

  useEffect(() => {
    setSelectedMessageIndex(null);
    setIsDecrypted(null);
    setDecryptedContent(null);
    reRender();
  }, [selectMessage]);

  const openMessage = useCallback(async (message) => {
    setSelectedMessage(message);
  }, []);

  return (
    <div className={'common-message-containder'}>

      {selectedMessageIndex === null &&
        < MessageBoxHeader handleOptionChange={handleOptionChange} reRender={reRender} selectedOption={selectedOption} messageList={messageList} readList={readList} unreadList={unreadList} pageType={type} renderList={renderMessageList} />
      }

      {messageViewStatus &&
        <ViewMessageHeader messageList={renderMessageList} reRender={reRender} index={selectedMessageIndex} setIndex={setSelectedMessageIndex} setIsDecrypted={setIsDecrypted} pageType={type} setDecryptedContent={setDecryptedContent} openMessage={openMessage} isDecrypted={isDecrypted} />
      }

      <div className={isMessageLoad ? 'message-box-container message-view-content' : 'message-box-container sort-inactive'} >
        {props.loader && <Loader isMain={true} text={props.text || 'Please wait, data is loading from the blockchain, Going forward it will be fast.'} />}
        {!props.loader && !messageViewStatus && renderMessageList.length !== 0 && <Messages messageList={renderMessageList} setSelectedMessageIndex={setSelectedMessageIndex} openMessage={openMessage} setIsDecrypted={setIsDecrypted} setDecryptedContent={setDecryptedContent} pageType={type} reRender={reRender} />}
        {!props.loader && messageViewStatus && !isDecrypted && renderMessageList.length !== 0 && <Encrypt reRender={reRender} messageList={renderMessageList} selectedMessage={selectedMessage} setSelectedMessageIndex={setSelectedMessageIndex} selectedIndex={selectedMessageIndex} openMessage={openMessage} setIsDecrypted={setIsDecrypted} setDecryptedContent={setDecryptedContent} pageType={type} />}
        {!props.loader && messageViewStatus && isDecrypted && renderMessageList.length !== 0 && <Decrypt messageList={renderMessageList} selectedMessage={selectedMessage} setSelectedMessageIndex={setSelectedMessageIndex} selectedIndex={selectedMessageIndex} openMessage={openMessage} setIsDecrypted={setIsDecrypted} pageType={type} decryptedContent={decryptedContent} reRender={reRender} />}
        {/* One condition, so a search or filter that matches nothing says so instead of leaving a
            blank panel — it used to be reported only when the raw mailbox was empty. */}
        {!props.loader && renderMessageList.length === 0 && (
          <NoMessage pageType={type} />
        )}
      </div>
      {isCompose &&
        <div >
          <ComposeEmail setIsCompose={setIsCompose} reRender={reRender} receiverName={receiverNameValue} pageType={type} />
        </div>
      }

    </div>
  )
}

export default CommonMessage;
